import { Button, Flex, Text, VStack } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToggleButton } from "src/components/Buttons/ToggleButton";
import {
  Item,
  SearchSelect,
} from "src/components/Inputs/SearchSelect/SearchSelect";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { SearchAndFilterTypes } from "src/constants";
import { ACTION_TYPE_OPTIONS, ACTIVITY_STATUS_OPTIONS } from "./labels";

interface ActivityFiltersProps {
  isOpen: boolean;
  createdByOptions: { id: string; name: string | null }[];
  showCreatedBy: boolean;
  onToggleFilter: () => void;
  onClearFilters: () => void;
}

const BASE_FILTER_PARAMS = ["actionType", "status"];

export const ActivityFilters: React.FC<ActivityFiltersProps> = ({
  isOpen,
  createdByOptions,
  showCreatedBy,
  onToggleFilter,
  onClearFilters,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDirty, setIsDirty] = useState(false);
  const [localSearchParams, setLocalSearchParams] = useState<URLSearchParams>(
    new URLSearchParams(searchParams)
  );

  useEffect(() => {
    setLocalSearchParams(new URLSearchParams(searchParams));
    setIsDirty(false);
  }, [searchParams]);

  const selectedActionTypes = useMemo(
    () => localSearchParams.get("actionType")?.split(",") ?? [],
    [localSearchParams]
  );
  const selectedCreatedById = localSearchParams.get("createdBy");
  const selectedStatuses = useMemo(
    () => localSearchParams.get("status")?.split(",") ?? [],
    [localSearchParams]
  );

  const createdByItems: Item[] = useMemo(
    () =>
      createdByOptions.map((option) => ({
        key: option.id,
        label: option.name ?? option.id,
      })),
    [createdByOptions]
  );

  const handleToggle = useCallback(
    (param: string, selected: string[], value: string) => {
      const newValues = selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value];

      const newParams = new URLSearchParams(localSearchParams);
      if (newValues.length === 0) {
        newParams.delete(param);
      } else {
        newParams.set(param, newValues.join(","));
      }
      newParams.delete(SearchAndFilterTypes.Offset);
      setLocalSearchParams(newParams);
      setIsDirty(true);
    },
    [localSearchParams]
  );

  const handleCreatedByChange = useCallback(
    (item: Item | null) => {
      const newParams = new URLSearchParams(localSearchParams);
      if (!item) {
        newParams.delete("createdBy");
      } else {
        newParams.set("createdBy", item.key);
      }
      newParams.delete(SearchAndFilterTypes.Offset);
      setLocalSearchParams(newParams);
      setIsDirty(true);
    },
    [localSearchParams]
  );

  const handleApplyFilters = useCallback(() => {
    setSearchParams(localSearchParams);
    setIsDirty(false);
    onToggleFilter();
  }, [localSearchParams, setSearchParams, onToggleFilter]);

  const handleClearAll = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    BASE_FILTER_PARAMS.forEach((param) => newParams.delete(param));
    if (showCreatedBy) {
      newParams.delete("createdBy");
    }
    newParams.delete(SearchAndFilterTypes.Offset);
    setSearchParams(newParams);
    setLocalSearchParams(newParams);
    setIsDirty(false);
    onClearFilters();
    onToggleFilter();
  }, [
    searchParams,
    setSearchParams,
    onClearFilters,
    onToggleFilter,
    showCreatedBy,
  ]);

  const hasActiveFilters =
    BASE_FILTER_PARAMS.some((param) => searchParams.get(param)) ||
    (showCreatedBy && Boolean(searchParams.get("createdBy")));

  return (
    <ResizableDrawer
      autosaveId="formActivityFilterDrawerWidth"
      isOpen={isOpen}
      onClose={onToggleFilter}
      header="Filter"
      footer={
        <>
          <Button
            onClick={handleClearAll}
            variant="outline"
            colorScheme="gray"
            flex="1"
            isDisabled={!hasActiveFilters && !isDirty}
          >
            Clear all
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="solid"
            colorScheme="primary"
            flex="1"
            isDisabled={!isDirty}
          >
            Apply
          </Button>
        </>
      }
    >
      <VStack align="stretch" spacing={6}>
        <VStack align="stretch" spacing={2}>
          <Text variant="filterLabel">Action</Text>
          <Flex flexWrap="wrap" gap={2}>
            {ACTION_TYPE_OPTIONS.map((option) => (
              <ToggleButton
                key={option.id}
                active={selectedActionTypes.includes(option.id)}
                onClick={() =>
                  handleToggle("actionType", selectedActionTypes, option.id)
                }
              >
                {option.label}
              </ToggleButton>
            ))}
          </Flex>
        </VStack>

        {showCreatedBy && (
          <VStack align="stretch" spacing={2}>
            <Text variant="filterLabel">Action taken by</Text>
            <SearchSelect
              label=""
              items={createdByItems}
              selectedItem={
                createdByItems.find(
                  (item) => item.key === selectedCreatedById
                ) ?? null
              }
              onChange={handleCreatedByChange}
              placeholder="Type a member name"
              variant="unlocked"
            />
          </VStack>
        )}

        <VStack align="stretch" spacing={2}>
          <Text variant="filterLabel">Status</Text>
          <Flex flexWrap="wrap" gap={2}>
            {ACTIVITY_STATUS_OPTIONS.map((option) => (
              <ToggleButton
                key={option.id}
                active={selectedStatuses.includes(option.id)}
                onClick={() =>
                  handleToggle("status", selectedStatuses, option.id)
                }
              >
                {option.label}
              </ToggleButton>
            ))}
          </Flex>
        </VStack>
      </VStack>
    </ResizableDrawer>
  );
};
